import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { DataGrid } from '@material-ui/data-grid';
import { useMultiFilter } from './hooks/useMultiFilter';
import MultiFilterPanel from './MultiFilterPanel';
import { applyMultiFilters } from './filterUtils';
import './CarwizDataGrid.css';

/**
 * CarwizDataGrid - Enhanced DataGrid with multi-filter support
 * 
 * Extends Material-UI DataGrid with an integrated multi-filter panel
 * that allows users to apply multiple filters simultaneously.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.rows - Array of data rows
 * @param {Array} props.columns - Array of column definitions
 * @param {boolean} [props.enableMultiFilter=true] - Enable/disable multi-filter panel
 * @param {Object} [props.filterPanelProps={}] - Additional props for MultiFilterPanel
 * @param {boolean} [props.loading] - Loading state
 * @param {boolean} [props.disabled] - Disabled state (passed to MultiFilterPanel as isSaving)
 * @param {Function} [props.onFilteredRowsChange] - Callback called with filtered rows whenever they change
 * @param {...any} props - All other props are passed through to DataGrid
 */
const CarwizDataGrid = ({
  rows,
  columns,
  enableMultiFilter = true,
  filterPanelProps = {},
  loading = false,
  disabled = false,
  onFilterModelChange,
  filterModel: externalFilterModel,
  onFilteredRowsChange,
  ...dataGridProps
}) => {
  // Multi-filter hook (manages filter state internally)
  const {
    filterItems,
    addFilter,
    removeFilter,
    updateFilter,
    clearAllFilters
  } = useMultiFilter();

  // Ref to track if we're updating filterModel from our side (to avoid sync loops)
  const isInternalUpdateRef = useRef(false);

  // Keep DataGrid filterModel in sync for compatibility (only first filter)
  const filterModel = useMemo(() => {
    if (filterItems.length > 0 && filterItems[0].columnField) {
      const firstFilter = filterItems[0];
      isInternalUpdateRef.current = true;
      return {
        items: [
          {
            columnField: firstFilter.columnField,
            operatorValue: firstFilter.operatorValue,
            value: firstFilter.value
          }
        ]
      };
    }
    isInternalUpdateRef.current = true;
    return { items: [] };
  }, [filterItems]);

  // Reset the ref after filterModel is set
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      // Use setTimeout to reset after React has processed the update
      const timer = setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [filterModel]);

  // Apply multi-filters to rows
  const filteredRows = useMemo(() => {
    return applyMultiFilters(rows, filterItems);
  }, [rows, filterItems]);

  // Notify parent component of filtered rows changes
  useEffect(() => {
    if (onFilteredRowsChange) {
      onFilteredRowsChange(filteredRows);
    }
  }, [filteredRows, onFilteredRowsChange]);

  // Handle DataGrid filter model changes (for compatibility with built-in filter)
  // Note: Primary filtering should be done through MultiFilterPanel
  // This syncs built-in filter changes back to our multi-filter system
  const handleFilterModelChange = useCallback(
    (newFilterModel) => {
      // Ignore updates that come from our own filterModel changes
      if (isInternalUpdateRef.current) {
        return;
      }

      // Sync built-in filter to our multi-filter system (only first filter)
      if (newFilterModel?.items && newFilterModel.items.length > 0) {
        const firstFilter = newFilterModel.items[0];
        // Update or add the first filter
        if (filterItems.length === 0) {
          // Add a filter, but we can't easily get the new ID synchronously
          // So we'll add it and update it in the next render cycle
          addFilter();
          // The filter will be updated on next render via useEffect or we handle it differently
          // For now, we'll rely on the user using MultiFilterPanel for the best experience
        } else if (filterItems[0]) {
          // Update existing first filter
          updateFilter(filterItems[0].id, {
            columnField: firstFilter.columnField,
            operatorValue: firstFilter.operatorValue,
            value: firstFilter.value
          });
        }
      } else if (filterItems.length > 0) {
        // If DataGrid filter is cleared, clear our filters too
        clearAllFilters();
      }

      // Call external handler if provided
      if (onFilterModelChange) {
        onFilterModelChange(newFilterModel);
      }
    },
    [filterItems, addFilter, updateFilter, clearAllFilters, onFilterModelChange]
  );

  return (
    <>
      {enableMultiFilter && (
        <MultiFilterPanel
          filterItems={filterItems}
          columns={columns}
          rows={rows}
          onAddFilter={addFilter}
          onRemoveFilter={removeFilter}
          onUpdateFilter={updateFilter}
          onClearAllFilters={clearAllFilters}
          isSaving={disabled}
          {...filterPanelProps}
        />
      )}
      <div className="carwiz-data-grid-container">
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          filterMode="server"
          filterModel={externalFilterModel || filterModel}
          onFilterModelChange={handleFilterModelChange}
          autoHeight={false}
          {...dataGridProps}
        />
      </div>
    </>
  );
};

export default CarwizDataGrid;
